import { useQuery } from "@apollo/client";
import {
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Typography,
} from "@mui/material";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { IsAuthenticatedContext } from "@/auth/Context";
import {
  CallOutcome,
  CallType,
  GetSingleCallRecordQuery,
  GetSingleCallRecordQueryVariables,
} from "@/graphql/graphql";
import { DefaultAppPage } from "@/layouts";
import { GET_SINGLE_CALL_RECORD } from "@/pages/UserDetails/components/UsageHistory/queries";
import { TRANSCRIBE_CALL } from "@/pages/UserDetails/queries";
import { ModalAudioPlayer, RecordLabel } from "@/pages/UserDetails/styles";
import { colors } from "@/styles/variables";

import HBATabs from "./components/scoringForm";
import { CallFeedback } from "./components/TranscriptFeedback";

const buildCallRecordPageHeading = (
  callRecordData?: GetSingleCallRecordQuery | null
): string | null => {
  const record = callRecordData?.callRecord;
  if (!record) {
    return null;
  }
  const account = record.account;
  const accountName = account
    ? [account.firstName, account.lastName].filter((part) => part?.trim()).join(" ")
    : "";
  const playlistTitle = record.playlist?.title?.trim() ?? "";
  const createdOn = record.createdOn;
  const callDateMs = createdOn ? new Date(createdOn).getTime() : NaN;
  const callDateFormatted =
    createdOn && !Number.isNaN(callDateMs)
      ? new Date(callDateMs).toLocaleString(undefined, {
          dateStyle: "full",
          timeStyle: "long",
        })
      : "";
  const parts = [accountName, playlistTitle, callDateFormatted].filter(Boolean);
  return parts.length ? parts.join(" - ") : null;
};

interface TranscriptSegment {
  text: string;
  start_time: number;
  end_time: number;
  speaker_label: string;
}

interface TranscriptSegmentProps {
  segment: TranscriptSegment;
  audioTime: number;
  onClick: () => void;
  nextSegment?: TranscriptSegment;
  index: number;
  transcriptData: TranscriptSegment[];
}

const TranscriptSegment: React.FC<TranscriptSegmentProps> = ({
  segment,
  audioTime,
  onClick,
  nextSegment,
  index,
  transcriptData,
}) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  const isHighlighted = audioTime >= segment.start_time && audioTime <= segment.end_time;
  const speakerChanges = nextSegment && segment.speaker_label !== nextSegment.speaker_label;
  const isPunctuation = /^[.!?,]$/.test(segment.text.trim());
  const shouldAddLineBreak = speakerChanges || /[.!?]$/.test(segment.text.trim());

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isActivityStart = () => {
    const nextEightSegments = transcriptData.slice(index, index + 4);
    const combinedText = nextEightSegments.map((s) => s.text.toLowerCase()).join(" ");
    return (
      (combinedText.includes("here") &&
        combinedText.includes("your") &&
        combinedText.includes("next activity")) ||
      (combinedText.includes("here") &&
        combinedText.includes("your") &&
        combinedText.includes("first activity")) ||
      combinedText.includes("here's your next activity") ||
      combinedText.includes("here is your next activity")
    );
  };
  // Check if this segment is or contains "Congratulations"
  const isOutroStart = () => {
    return segment.text.includes("Congratulations") || segment.text.includes("congratulations");
  };

  // More specific check for "three times per week" phrase
  const containsTimesPerWeek = () => {
    return segment.text.toLowerCase().includes("week");
  };

  // Check if this segment ends with period and contains word "week"
  const isEndOfWeekStatement = () => {
    return segment.text.trim().endsWith(".") && segment.text.toLowerCase().includes("week");
  };

  const handleDoubleClick = () => {
    if (spanRef.current) {
      spanRef.current.focus();
    }
  };

  const handleBlur = () => {
    if (spanRef.current) {
      spanRef.current.contentEditable = "false";
    }
  };

  const isCongratsText =
    segment.text.includes("Congratulations") || segment.text.includes("congratulations");

  return (
    <>
      {isOutroStart() && (
        <>
          <br />
        </>
      )}
      <span
        ref={spanRef}
        data-index={index}
        className={isCongratsText ? "startOutro" : ""}
        onClick={onClick}
        onDoubleClick={handleDoubleClick}
        onBlur={handleBlur}
        onKeyDown={(e) => e.key === "Enter" && spanRef.current?.blur()}
        style={{
          color: segment.speaker_label === "clinician" ? "blue" : "red",
          backgroundColor: isHighlighted ? "yellow" : "transparent",
          padding: "0 2px",
          margin: "0 1px",
          display: "inline",
          transition: "all 0.2s ease",
        }}
      >
        {isPunctuation ? segment.text : ` ${segment.text}`}
      </span>
      {shouldAddLineBreak && <br />}

      {containsTimesPerWeek() && isEndOfWeekStatement() && <br />}
    </>
  );
};

const CallRecord = () => {
  const [audioTime, setAudioTime] = useState<number>(0);
  const [showScore, setShowScore] = useState<boolean>(false);
  const [sections, setSections] = useState<
    { element: Element; index: number; exactMatchIndex: number }[]
  >([]);
  const [feedbackSection, setFeedbackSection] = useState<{
    element: Element | null;
    index: number;
  }>({
    element: null,
    index: -1,
  });
  const [outroSection, setOutroSection] = useState<{
    element: Element | null;
    index: number;
  }>({
    element: null,
    index: -1,
  });
  const [transcriptData, setTranscriptData] = useState<TranscriptSegment[]>([]);
  const [programRating, setProgramRating] = useState<number | null>(null);
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isAuthenticated, setIsAuthenticated] = useContext(IsAuthenticatedContext);
  const { id, date, aid, rid } = useParams();
  const audioElementRef = useRef<HTMLAudioElement>(null);
  const transcriptContainerRef = useRef<HTMLDivElement>(null);

  const {
    data: callTranscribeData,
    loading: transcribeLoading,
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    refetch: refetchData,
  } = useQuery(TRANSCRIBE_CALL, {
    variables: { callSid: id },
    fetchPolicy: "network-only",
  });

  const {
    data: callRecordData,
    loading: recordLoading,
    refetch: refetchCallRecord,
  } = useQuery<GetSingleCallRecordQuery, GetSingleCallRecordQueryVariables>(
    GET_SINGLE_CALL_RECORD,
    {
      skip: !id,
      // Guaranteed to be a string because of the skip
      variables: { callSid: id as string },
      fetchPolicy: "network-only",
    }
  );

  // Find the exact phrase that starts an activity
  const findExactPhrase = (startIndex: number, transcriptData: TranscriptSegment[]): number => {
    const phrasesToFind = [
      "here is your next activity",
      "here's your next activity",
      "here is your first activity",
      "here's your first activity",
    ];

    // Look through a window of segments around the suspected index
    for (
      let i = Math.max(0, startIndex - 5);
      i < Math.min(startIndex + 10, transcriptData.length);
      i++
    ) {
      // Check if this single segment contains the phrase
      const singleSegmentText = transcriptData[i].text.toLowerCase();
      if (phrasesToFind.some((phrase) => singleSegmentText.includes(phrase))) {
        return i; // Return exact match if found in a single segment
      }

      // Also check a few segments together (more reliable detection)
      const windowSize = 4; // Look at up to 4 consecutive segments
      for (let j = 0; j < windowSize && i + j < transcriptData.length; j++) {
        // Build combined text from segments i to i+j
        const combinedText = transcriptData
          .slice(i, i + j + 1)
          .map((s) => s.text.toLowerCase())
          .join(" ")
          .replace(/\s+/g, " ")
          .trim();

        // If found, determine which segment actually starts the phrase
        if (phrasesToFind.some((phrase) => combinedText.includes(phrase))) {
          // Find the first segment that contains any of these key words
          for (let k = i; k <= i + j; k++) {
            if (transcriptData[k].text.toLowerCase().includes("here")) {
              return k; // Return the segment that starts with "here"
            }
          }
          return i; // Fallback to the first segment
        }
      }
    }

    return startIndex; // Return original index if exact phrase not found
  };

  const detectSections = useCallback((transcriptData: TranscriptSegment[]) => {
    const sections: {
      type: "activity" | "feedback" | "outro";
      index: number;
      exactMatchIndex?: number;
    }[] = [];

    // Look for activity indicators with a wider window and more flexible pattern matching
    for (let i = 0; i < transcriptData.length; i++) {
      // Create a longer window of text to analyze (8 segments instead of 4)
      const nextEightSegments = transcriptData.slice(i, i + 5);
      const combinedText = nextEightSegments.map((s) => s.text.toLowerCase()).join(" ");

      // More flexible pattern matching for activity indicators
      if (
        (combinedText.includes("here") &&
          combinedText.includes("your") &&
          combinedText.includes("next activity")) ||
        (combinedText.includes("here") &&
          combinedText.includes("your") &&
          combinedText.includes("first activity")) ||
        // Add the exact phrase that's in your screenshot
        combinedText.includes("here is your next activity")
      ) {
        const exactMatchIndex = findExactPhrase(i, transcriptData);
        sections.push({ type: "activity", index: i, exactMatchIndex });
        i += 2;
        continue;
      }

      // Rest of the function remains the same for feedback and outro detection
      if (
        combinedText.includes("time to reflect") &&
        combinedText.includes("on the") &&
        combinedText.includes("activities")
      ) {
        sections.push({ type: "feedback", index: i });
      }

      if (
        combinedText.includes("congratulations") ||
        combinedText.includes("finished the activities") ||
        combinedText.includes("thank you for your time") ||
        combinedText.includes("that concludes our session") ||
        combinedText.includes("that's all for today") ||
        combinedText.includes("we've reached the end") ||
        combinedText.includes("this concludes our call")
      ) {
        sections.push({ type: "outro", index: i });
      }
    }

    return sections;
  }, []);

  // New function to specifically detect "Congratulations" for outro navigation
  const detectOutroSpecifically = useCallback((transcriptData: TranscriptSegment[]): number => {
    // Look specifically for "Congratulations" in the transcript
    for (let i = 0; i < transcriptData.length; i++) {
      if (
        transcriptData[i].text.includes("Congratulations") ||
        transcriptData[i].text.includes("congratulations")
      ) {
        return i;
      }
    }

    // Fallback to the general outro detection if "Congratulations" isn't found
    for (let i = 0; i < transcriptData.length; i++) {
      const nextFourSegments = transcriptData.slice(i, i + 4);
      const combinedText = nextFourSegments.map((s) => s.text.toLowerCase()).join(" ");

      if (
        combinedText.includes("thank you for your time") ||
        combinedText.includes("that concludes our session") ||
        combinedText.includes("that's all for today") ||
        combinedText.includes("we've reached the end") ||
        combinedText.includes("this concludes our call") ||
        combinedText.includes("finished the activities")
      ) {
        return i;
      }
    }

    return -1;
  }, []);

  const setTableOfContents = useCallback(() => {
    if (transcriptData.length) {
      const detectedSections = detectSections(transcriptData);
      const activitySections: {
        element: Element;
        index: number;
        exactMatchIndex: number;
      }[] = [];
      let feedback: { element: Element | null; index: number } = {
        element: null,
        index: -1,
      };

      // First detect sections as normal
      detectedSections.forEach(({ type, index, exactMatchIndex }) => {
        // Use the exact match index if it exists
        const targetIndex = typeof exactMatchIndex !== "undefined" ? exactMatchIndex : index;
        const span = document.querySelector(`span[data-index="${targetIndex}"]`);

        if (span) {
          if (type === "activity") {
            span.className = "startParagraph";
            activitySections.push({
              element: span,
              index: targetIndex,
              exactMatchIndex: targetIndex,
            });
          } else if (type === "feedback") {
            span.className = "startFeedback";
            feedback = { element: span, index: targetIndex };
          }
        }
      });

      // Special handling for outro - find "Congratulations" specifically
      const outroIndex = detectOutroSpecifically(transcriptData);
      let outro: { element: Element | null; index: number } = {
        element: null,
        index: -1,
      };

      if (outroIndex !== -1) {
        const outroSpan = document.querySelector(`span[data-index="${outroIndex}"]`);
        if (outroSpan) {
          outroSpan.className = "startOutro";
          outro = { element: outroSpan, index: outroIndex };
        }
      }

      setSections(activitySections);
      setFeedbackSection(feedback);
      setOutroSection(outro);
    }
  }, [
    transcriptData,
    detectOutroSpecifically,
    detectSections,
    setSections,
    setFeedbackSection,
    setOutroSection,
  ]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "logout") {
        localStorage.clear();
        setIsAuthenticated(false);
        window.location.href = "/login";
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [setIsAuthenticated]);

  useEffect(() => {
    if (callTranscribeData?.transcribe?.data?.transcription?.[0]) {
      try {
        const rawData =
          callTranscribeData?.transcribe?.data?.transcription[0]?.processed_transcripts;
        const correctedData = rawData.replace(/NaN/g, "null");
        const processedData = JSON.parse(correctedData);
        setTranscriptData(processedData);
      } catch (error) {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line no-console
        console.error("Error parsing transcript data:", error);
      }
    }
  }, [callTranscribeData]);

  useEffect(() => {
    if (document.getElementsByTagName("div")[5]) {
      document.getElementsByTagName("div")[5].style.maxWidth = "100%";
    }
  }, [callTranscribeData]);

  useEffect(() => {
    const shouldShowScore = !(
      callRecordData?.callRecord?.type === CallType.InboundCallBdl ||
      callRecordData?.callRecord?.type === CallType.OutboundCallBdl ||
      callRecordData?.callRecord?.outcome !== CallOutcome.Success ||
      recordLoading
    );
    setShowScore(shouldShowScore);
  }, [callRecordData, recordLoading]);

  useEffect(() => {
    setTableOfContents();
  }, [transcriptData, setTableOfContents]);

  const jumpToTime = (time: number) => {
    if (!isNaN(time) && audioElementRef.current) {
      audioElementRef.current.currentTime = time;
    }
  };

  const handleAudioMount = (audio: HTMLAudioElement) => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (audioElementRef as any).current = audio;
  };

  // Improved scroll function that positions the element properly
  const scrollToElement = (element: Element | null, behavior: ScrollBehavior = "smooth") => {
    if (element && transcriptContainerRef.current) {
      // Get the position of the element relative to the transcript container
      const containerRect = transcriptContainerRef.current.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      // Calculate the scroll position to place the element near the top with a slight offset
      const scrollTop =
        transcriptContainerRef.current.scrollTop + (elementRect.top - containerRect.top) - 170; // Adjusted offset

      // Scroll to the calculated position
      transcriptContainerRef.current.scrollTo({
        top: scrollTop,
        behavior: behavior,
      });
    }
  };

  // Add line breaks after an activity section is found
  const addActivityBreaks = useCallback(() => {
    if (sections.length > 0 && transcriptContainerRef.current) {
      sections.forEach((section) => {
        const element = section.element;
        if (element) {
          // Add a line break before the activity span
          const br = document.createElement("br");
          element.parentNode?.insertBefore(br, element);

          // Add a second line break for more spacing
          const br2 = document.createElement("br");
          element.parentNode?.insertBefore(br2, element);
        }
      });
    }
  }, [sections]);

  // Call this after sections are set
  useEffect(() => {
    // Small delay to ensure DOM is ready
    setTimeout(addActivityBreaks, 100);
  }, [sections, addActivityBreaks]);

  useEffect(() => {
    if (callTranscribeData?.transcribe?.data?.transcription?.[0]) {
      try {
        const text = callTranscribeData.transcribe.data.transcription[0].text;
        if (text) {
          const normalizedText = text
            .replace(/([a-z])([A-Z])/g, "$1 $2")
            .replace(/(\d)([A-Za-z])/g, "$1 $2")
            .replace(/([A-Za-z])(\d)/g, "$1 $2");

          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line no-useless-escape
          const regex = /so far[\.,?\s]+(.*?)(?=\s*Question|\s*$)/i;
          const match = normalizedText.match(regex);

          if (match) {
            const followingText = match[1].toLowerCase().trim();

            if (followingText.includes("question")) {
              setProgramRating(null);
              return;
            }

            const numberWords = {
              one: 1,
              two: 2,
              three: 3,
              four: 4,
              five: 5,
              One: 1,
              Two: 2,
              Three: 3,
              Four: 4,
              Five: 5,
            };

            for (const [word, value] of Object.entries(numberWords)) {
              if (followingText.includes(word)) {
                setProgramRating(value);
                return;
              }
            }

            const numberMatch = followingText.match(/[1-5]/);
            if (numberMatch) {
              setProgramRating(Number(numberMatch[0]));
              return;
            }
          }
          setProgramRating(null);
        }
      } catch (error) {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line no-console
        console.error("Error parsing program rating:", error);
        setProgramRating(null);
      }
    }
  }, [callTranscribeData]);

  const callRecordHeading = buildCallRecordPageHeading(callRecordData);

  return (
    <DefaultAppPage>
      <div style={{ display: "grid", gridTemplateColumns: "10% 40% 50%" }}>
        {/* Navigation Sidebar */}
        <div className="contentsContainer" style={{ width: "6rem", paddingTop: "40px" }}>
          {sections.length > 0 && (
            <div className="tableOfContents" style={{ position: "fixed" }}>
              <List
                sx={{ width: "100%", bgcolor: colors.gray2 }}
                component="nav"
                aria-labelledby="list-header"
                subheader={
                  <ListSubheader component="div" id="list-header">
                    Contents
                  </ListSubheader>
                }
              >
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => {
                      document
                        .getElementById("classSidHeader")
                        ?.scrollIntoView({ behavior: "smooth" });
                      if (transcriptData.length > 0) {
                        jumpToTime(transcriptData[0].start_time);
                      }
                    }}
                  >
                    <ListItemText primary="Intro" />
                  </ListItemButton>
                </ListItem>
                {sections.map((section, index) => (
                  <ListItem key={index} disablePadding>
                    <ListItemButton
                      onClick={() => {
                        scrollToElement(section.element);
                        if (
                          !isNaN(section.exactMatchIndex) &&
                          transcriptData[section.exactMatchIndex]
                        ) {
                          jumpToTime(transcriptData[section.exactMatchIndex].start_time);
                        }
                      }}
                    >
                      <ListItemText primary={`Activity ${index + 1}`} />
                    </ListItemButton>
                  </ListItem>
                ))}
                {feedbackSection.element && (
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => {
                        scrollToElement(feedbackSection.element);
                        if (
                          !isNaN(feedbackSection.index) &&
                          transcriptData[feedbackSection.index]
                        ) {
                          jumpToTime(transcriptData[feedbackSection.index].start_time);
                        }
                      }}
                    >
                      <ListItemText primary="Feedback" />
                    </ListItemButton>
                  </ListItem>
                )}
                {outroSection.element && (
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => {
                        scrollToElement(outroSection.element);
                        if (!isNaN(outroSection.index) && transcriptData[outroSection.index]) {
                          jumpToTime(transcriptData[outroSection.index].start_time);
                        }
                      }}
                    >
                      <ListItemText primary="Outro" />
                    </ListItemButton>
                  </ListItem>
                )}
              </List>
            </div>
          )}
        </div>

        {/* Main Transcript Area */}
        <div
          ref={transcriptContainerRef}
          style={{
            width: "100%",
            height: "90vh",
            overflowY: "scroll",
            overflowX: "hidden",
          }}
        >
          <div
            style={{
              padding: "40px",
              maxWidth: "800px",
              position: "relative",
              minWidth: "50%",
              background: "white",
            }}
          >
            {callRecordHeading && (
              <Typography
                component="h1"
                sx={{
                  color: "#c06c9a",
                  fontSize: "1.85rem",
                  fontWeight: 500,
                  lineHeight: 1.35,
                  margin: 0,
                  marginBottom: "1.5rem",
                  paddingTop: "0.25rem",
                }}
              >
                {callRecordHeading}
              </Typography>
            )}

            <div
              className="audioControls"
              style={{
                position: "sticky",
                top: 0,
                background: "white",
                zIndex: 1,
                padding: "20px 0",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
                <ModalAudioPlayer
                  src={`https://api.twilio.com/${date}/Accounts/${aid}/Recordings/${rid}`}
                  ref={handleAudioMount}
                  onTimeUpdate={(e: React.ChangeEvent<HTMLAudioElement>) =>
                    setAudioTime(e.currentTarget.currentTime)
                  }
                  controls
                  preload="metadata"
                />
              </div>
            </div>

            {/* Call Information */}
            <RecordLabel id="classSidHeader">Call Sid</RecordLabel>
            <div>{id}</div>

            <RecordLabel style={{ marginTop: "15px" }}>CSAT</RecordLabel>
            <div style={{ fontSize: "16px", marginTop: "5px" }}>
              {programRating ? programRating : ""}
            </div>

            {callRecordData?.callRecord.callDeliverySpeed && (
              <>
                <RecordLabel style={{ marginTop: "15px" }}>Call Delivery Speed</RecordLabel>
                <div style={{ fontSize: "16px", marginTop: "5px" }}>
                  {callRecordData?.callRecord.callDeliverySpeed}
                </div>
              </>
            )}

            {/* Transcript Content */}
            <div id="transcript" className="transcript" style={{ marginTop: "20px" }}>
              {transcribeLoading ? (
                <CircularProgress />
              ) : (
                transcriptData?.map((segment, index) => (
                  <TranscriptSegment
                    key={index}
                    segment={segment}
                    audioTime={audioTime}
                    onClick={() => jumpToTime(segment.start_time)}
                    nextSegment={
                      index < transcriptData.length - 1 ? transcriptData[index + 1] : undefined
                    }
                    index={index}
                    transcriptData={transcriptData}
                  />
                ))
              )}
            </div>
            {transcriptData && transcriptData.length > 0 && (
              <div style={{ marginTop: "40px", position: "relative", zIndex: "1" }}>
                <CallFeedback callSid={id ? id : ""} />
              </div>
            )}
          </div>
        </div>

        {/* Scoring Section */}
        {showScore && (
          <div style={{ width: "100%", height: "90vh", overflowY: "scroll" }}>
            <div style={{ padding: "20px", background: "white" }}>
              <HBATabs
                sid={id}
                data={callRecordData}
                loading={recordLoading}
                refetch={refetchCallRecord}
              />
            </div>
          </div>
        )}
      </div>
    </DefaultAppPage>
  );
};

export default CallRecord;
